
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/hooks/useLanguage";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import RedirectHandler from "@/components/RedirectHandler";
import LegalChatbot from "@/components/LegalChatbot";
import FloatingPhoneButton from "@/components/FloatingPhoneButton";
import AdminRoute from "@/components/admin/AdminRoute";
import { generalRoutes, practiceAreaRoutes, immigrationRoutes } from "@/lib/route-mapping";
import { otherRoutes, areaRoutes, criminalDefenseRoutes, familyLawRoutes, personalInjuryRoutes, realEstateRoutes, businessLawRoutes, probateEstateRoutes, taxLawRoutes, blogRoutes } from "@/lib/route-mapping-extended";
import Index from "./pages/Index";
import AboutUs from "./pages/AboutUs";
import PracticeAreas from "./pages/PracticeAreas";
import ImmigrationServices from "./pages/ImmigrationServices";
import AsylumServices from "./pages/immigration/AsylumServices";
import CitizenshipNaturalization from "./pages/immigration/CitizenshipNaturalization";
import FamilyBasedImmigration from "./pages/immigration/FamilyBasedImmigration";
import EmploymentBasedImmigration from "./pages/immigration/EmploymentBasedImmigration";
import DeportationRemovalDefense from "./pages/immigration/DeportationRemovalDefense";
import Waivers from "./pages/immigration/Waivers";
import DACA from "./pages/immigration/DACA";
import K1FianceVisa from "./pages/immigration/K1FianceVisa";
import VAWAPetitions from "./pages/immigration/VAWAPetitions";
import HumanitarianParoleTPS from "./pages/immigration/HumanitarianParoleTPS";
import GreenCardRenewal from "./pages/immigration/GreenCardRenewal";
import ConsularProcessing from "./pages/immigration/ConsularProcessing";
import UVisa from "./pages/immigration/UVisa";
import AdvanceParole from "./pages/immigration/AdvanceParole";
import ReEntryPermit from "./pages/immigration/ReEntryPermit";
import EB5InvestorVisa from "./pages/immigration/EB5InvestorVisa";
import StudentVisas from "./pages/immigration/StudentVisas";
import CriminalDefense from "./pages/CriminalDefense";
import DWI from "./pages/criminal-defense/DWI";
import DrugCharges from "./pages/criminal-defense/DrugCharges";
import AssaultFamilyViolence from "./pages/criminal-defense/AssaultFamilyViolence";
import ViolentCrimes from "./pages/criminal-defense/ViolentCrimes";
import TheftRobbery from "./pages/criminal-defense/TheftRobbery";
import SexCrimes from "./pages/criminal-defense/SexCrimes";
import ExpunctionNondisclosure from "./pages/criminal-defense/ExpunctionNondisclosure";
import PersonalInjury from "./pages/PersonalInjury";
import AutoAccident from "./pages/personal-injury/AutoAccident";
import TruckAccident from "./pages/personal-injury/TruckAccident";
import IndustrialAccident from "./pages/personal-injury/IndustrialAccident";
import MedicalMalpractice from "./pages/personal-injury/MedicalMalpractice";
import Divorce from "./pages/family-law/Divorce";
import ChildCustody from "./pages/family-law/ChildCustody";
import ChildSupport from "./pages/family-law/ChildSupport";
import CPSDefense from "./pages/family-law/CPSDefense";
import Adoption from "./pages/family-law/Adoption";
import CourtOrderModification from "./pages/family-law/CourtOrderModification";
import FamilyLaw from "./pages/FamilyLaw";
import TaxLaw from "./pages/TaxLaw";
import TaxLitigation from "./pages/tax/TaxLitigation";
import TaxPlanning from "./pages/tax/TaxPlanning";
import TaxDebtResolution from "./pages/tax/TaxDebtResolution";
import ProbateEstate from "./pages/ProbateEstate";
import ProbateAdministration from "./pages/probate-estate/ProbateAdministration";
import EstatePlanning from "./pages/probate-estate/EstatePlanning";
import SpecialNeedsTrust from "./pages/probate-estate/SpecialNeedsTrust";
import Guardianship from "./pages/probate-estate/Guardianship";
import RealEstateLaw from "./pages/RealEstateLaw";
import BuyingSelling from "./pages/real-estate/BuyingSelling";
import TitleSearch from "./pages/real-estate/TitleSearch";
import ContractReview from "./pages/real-estate/ContractReview";
import AffidavitsHeirship from "./pages/real-estate/AffidavitsHeirship";
import LienRelease from "./pages/real-estate/LienRelease";
import LeaseNegotiations from "./pages/real-estate/LeaseNegotiations";
import Closings from "./pages/real-estate/Closings";
import ForeclosuresEvictions from "./pages/real-estate/ForeclosuresEvictions";
import BusinessLaw from "./pages/BusinessLaw";
import BusinessFormation from "./pages/business-law/BusinessFormation";
import PartnershipAgreements from "./pages/business-law/PartnershipAgreements";
import TrademarkRegistration from "./pages/business-law/TrademarkRegistration";
import PurchaseSaleBusiness from "./pages/business-law/PurchaseSaleBusiness";
import RegisteredAgent from "./pages/business-law/RegisteredAgent";
import DebtCollection from "./pages/business-law/DebtCollection";
import InsuranceClaims from "./pages/business-law/InsuranceClaims";
import NonprofitFormation from "./pages/business-law/NonprofitFormation";
import BusinessClosing from "./pages/business-law/BusinessClosing";
import Contact from "./pages/Contact";
import Booking from "./pages/Booking";
import BookingSuccess from "./pages/booking/Success";
import Tools from "./pages/Tools";
import Blog from "./pages/Blog";
import AsylumProcess2025 from "./pages/blog/AsylumProcess2025";
import DeportationDefense from "./pages/blog/DeportationDefense";
import CitizenshipGuide from "./pages/blog/CitizenshipGuide";
import PoliceRights from "./pages/blog/PoliceRights";
import PersonalInjuryClaim from "./pages/blog/PersonalInjuryClaim";
import DivorceTexas from "./pages/blog/DivorceTexas";
import DynamicBlogPost from "./pages/blog/DynamicBlogPost";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import NotFound from "./pages/NotFound";
import TheWoodlands from "./pages/areas/TheWoodlands";
import RiverOaks from "./pages/areas/RiverOaks";
import Montrose from "./pages/areas/Montrose";
import Heights from "./pages/areas/Heights";
import WestUniversity from "./pages/areas/WestUniversity";
import SugarLand from "./pages/areas/SugarLand";
import Katy from "./pages/areas/Katy";
import Midtown from "./pages/areas/Midtown";
import MedicalCenter from "./pages/areas/MedicalCenter";
import Memorial from "./pages/areas/Memorial";
import Cypress from "./pages/areas/Cypress";
import Uptown from "./pages/areas/Uptown";
import Downtown from "./pages/areas/Downtown";
import EnergyCorridor from "./pages/areas/EnergyCorridor";
import GreaterSouthwest from "./pages/areas/GreaterSouthwest";
import SpringBranch from "./pages/areas/SpringBranch";
import AdminBookings from "./pages/admin/Bookings";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminAppointments from "./pages/admin/Appointments";
import AdminCalendar from "./pages/admin/Calendar";
import AdminBlog from "./pages/admin/Blog";
import AdminPages from "./pages/admin/Pages";
import AdminPayments from "./pages/admin/Payments";
import AdminIntegrations from "./pages/admin/Integrations";
import AdminSettings from "./pages/admin/Settings";
import SEOValidation from "./pages/admin/SEOValidation";
import AdminLogin from "./pages/AdminLogin";
import AdminResetPassword from "./pages/AdminResetPassword";
import AdminLayout from "./components/admin/AdminLayout";
import SEO from "@/components/SEO";
import URLIndex from "./pages/admin/URLIndex";
import SiteMap from "./pages/admin/SiteMap";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <BrowserRouter>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <ScrollToTop />
            <RedirectHandler />
            <LegalChatbot />
            <FloatingPhoneButton />
            <SEO />
            <Routes>
              {/* Home - All Languages */}
              <Route path={generalRoutes.home.en} element={<Index />} />
              <Route path={generalRoutes.home.es} element={<Index />} />
              <Route path={generalRoutes.home.vi} element={<Index />} />
              
              {/* About - All Languages */}
              <Route path={generalRoutes.about.en} element={<AboutUs />} />
              <Route path={generalRoutes.about.es} element={<AboutUs />} />
              <Route path={generalRoutes.about.vi} element={<AboutUs />} />
              
              {/* Practice Areas - All Languages */}
              <Route path={generalRoutes.practiceAreas.en} element={<PracticeAreas />} />
              <Route path={generalRoutes.practiceAreas.es} element={<PracticeAreas />} />
              <Route path={generalRoutes.practiceAreas.vi} element={<PracticeAreas />} />
              
              {/* Contact - All Languages */}
              <Route path={generalRoutes.contact.en} element={<Contact />} />
              <Route path={generalRoutes.contact.es} element={<Contact />} />
              <Route path={generalRoutes.contact.vi} element={<Contact />} />
              
              {/* Consultation routes now redirect to Booking (handled by seo-redirects) */}
              {/* Keeping Booking routes only */}
              
              {/* Booking - All Languages */}
              <Route path={generalRoutes.booking.en} element={<Booking />} />
              <Route path={generalRoutes.booking.es} element={<Booking />} />
              <Route path={generalRoutes.booking.vi} element={<Booking />} />
              
              {/* Blog - All Languages */}
              <Route path={generalRoutes.blog.en} element={<Blog />} />
              <Route path={generalRoutes.blog.es} element={<Blog />} />
              <Route path={generalRoutes.blog.vi} element={<Blog />} />
              
              {/* Immigration Services - All Languages */}
              <Route path={practiceAreaRoutes.immigration.en} element={<ImmigrationServices />} />
              <Route path={practiceAreaRoutes.immigration.es} element={<ImmigrationServices />} />
              <Route path={practiceAreaRoutes.immigration.vi} element={<ImmigrationServices />} />
              
              {/* Criminal Defense - All Languages */}
              <Route path={practiceAreaRoutes.criminal.en} element={<CriminalDefense />} />
              <Route path={practiceAreaRoutes.criminal.es} element={<CriminalDefense />} />
              <Route path={practiceAreaRoutes.criminal.vi} element={<CriminalDefense />} />
              
              {/* Personal Injury - All Languages */}
              <Route path={practiceAreaRoutes.personalInjury.en} element={<PersonalInjury />} />
              <Route path={practiceAreaRoutes.personalInjury.es} element={<PersonalInjury />} />
              <Route path={practiceAreaRoutes.personalInjury.vi} element={<PersonalInjury />} />
              
              {/* Family Law - All Languages */}
              <Route path={practiceAreaRoutes.family.en} element={<FamilyLaw />} />
              <Route path={practiceAreaRoutes.family.es} element={<FamilyLaw />} />
              <Route path={practiceAreaRoutes.family.vi} element={<FamilyLaw />} />
              
              {/* Tax Law - All Languages */}
              <Route path={practiceAreaRoutes.tax.en} element={<TaxLaw />} />
              <Route path={practiceAreaRoutes.tax.es} element={<TaxLaw />} />
              <Route path={practiceAreaRoutes.tax.vi} element={<TaxLaw />} />
              
              {/* Probate & Estate - All Languages */}
              <Route path={practiceAreaRoutes.probate.en} element={<ProbateEstate />} />
              <Route path={practiceAreaRoutes.probate.es} element={<ProbateEstate />} />
              <Route path={practiceAreaRoutes.probate.vi} element={<ProbateEstate />} />
              
              {/* Real Estate Law - All Languages */}
              <Route path={practiceAreaRoutes.realEstate.en} element={<RealEstateLaw />} />
              <Route path={practiceAreaRoutes.realEstate.es} element={<RealEstateLaw />} />
              <Route path={practiceAreaRoutes.realEstate.vi} element={<RealEstateLaw />} />
              
              {/* Business Law - All Languages */}
              <Route path={practiceAreaRoutes.business.en} element={<BusinessLaw />} />
              <Route path={practiceAreaRoutes.business.es} element={<BusinessLaw />} />
              <Route path={practiceAreaRoutes.business.vi} element={<BusinessLaw />} />
              
              {/* Immigration Sub-Pages - All Languages */}
              <Route path={immigrationRoutes.asylumPage.en} element={<AsylumServices />} />
              <Route path={immigrationRoutes.asylumPage.es} element={<AsylumServices />} />
              <Route path={immigrationRoutes.asylumPage.vi} element={<AsylumServices />} />
              
              <Route path={immigrationRoutes.citizenship.en} element={<CitizenshipNaturalization />} />
              <Route path={immigrationRoutes.citizenship.es} element={<CitizenshipNaturalization />} />
              <Route path={immigrationRoutes.citizenship.vi} element={<CitizenshipNaturalization />} />
              
              <Route path={immigrationRoutes.familyBased.en} element={<FamilyBasedImmigration />} />
              <Route path={immigrationRoutes.familyBased.es} element={<FamilyBasedImmigration />} />
              <Route path={immigrationRoutes.familyBased.vi} element={<FamilyBasedImmigration />} />
              
              <Route path={immigrationRoutes.employmentBased.en} element={<EmploymentBasedImmigration />} />
              <Route path={immigrationRoutes.employmentBased.es} element={<EmploymentBasedImmigration />} />
              <Route path={immigrationRoutes.employmentBased.vi} element={<EmploymentBasedImmigration />} />
              
              <Route path={immigrationRoutes.deportation.en} element={<DeportationRemovalDefense />} />
              <Route path={immigrationRoutes.deportation.es} element={<DeportationRemovalDefense />} />
              <Route path={immigrationRoutes.deportation.vi} element={<DeportationRemovalDefense />} />
              
              <Route path={immigrationRoutes.waivers.en} element={<Waivers />} />
              <Route path={immigrationRoutes.waivers.es} element={<Waivers />} />
              <Route path={immigrationRoutes.waivers.vi} element={<Waivers />} />
              
              <Route path={immigrationRoutes.daca.en} element={<DACA />} />
              <Route path={immigrationRoutes.daca.es} element={<DACA />} />
              <Route path={immigrationRoutes.daca.vi} element={<DACA />} />
              
              <Route path={immigrationRoutes.k1Visa.en} element={<K1FianceVisa />} />
              <Route path={immigrationRoutes.k1Visa.es} element={<K1FianceVisa />} />
              <Route path={immigrationRoutes.k1Visa.vi} element={<K1FianceVisa />} />
              
              <Route path={immigrationRoutes.vawa.en} element={<VAWAPetitions />} />
              <Route path={immigrationRoutes.vawa.es} element={<VAWAPetitions />} />
              <Route path={immigrationRoutes.vawa.vi} element={<VAWAPetitions />} />
              
              <Route path={immigrationRoutes.humanitarianParole.en} element={<HumanitarianParoleTPS />} />
              <Route path={immigrationRoutes.humanitarianParole.es} element={<HumanitarianParoleTPS />} />
              <Route path={immigrationRoutes.humanitarianParole.vi} element={<HumanitarianParoleTPS />} />
              
              <Route path={immigrationRoutes.greenCardRenewal.en} element={<GreenCardRenewal />} />
              <Route path={immigrationRoutes.greenCardRenewal.es} element={<GreenCardRenewal />} />
              <Route path={immigrationRoutes.greenCardRenewal.vi} element={<GreenCardRenewal />} />
              
              <Route path={immigrationRoutes.consularProcessing.en} element={<ConsularProcessing />} />
              <Route path={immigrationRoutes.consularProcessing.es} element={<ConsularProcessing />} />
              <Route path={immigrationRoutes.consularProcessing.vi} element={<ConsularProcessing />} />
              
              <Route path={immigrationRoutes.uVisa.en} element={<UVisa />} />
              <Route path={immigrationRoutes.uVisa.es} element={<UVisa />} />
              <Route path={immigrationRoutes.uVisa.vi} element={<UVisa />} />
              
              <Route path={immigrationRoutes.advanceParole.en} element={<AdvanceParole />} />
              <Route path={immigrationRoutes.advanceParole.es} element={<AdvanceParole />} />
              <Route path={immigrationRoutes.advanceParole.vi} element={<AdvanceParole />} />
              
              <Route path={immigrationRoutes.reentryPermit.en} element={<ReEntryPermit />} />
              <Route path={immigrationRoutes.reentryPermit.es} element={<ReEntryPermit />} />
              <Route path={immigrationRoutes.reentryPermit.vi} element={<ReEntryPermit />} />
              
              <Route path={immigrationRoutes.eb5.en} element={<EB5InvestorVisa />} />
              <Route path={immigrationRoutes.eb5.es} element={<EB5InvestorVisa />} />
              <Route path={immigrationRoutes.eb5.vi} element={<EB5InvestorVisa />} />
              
              <Route path={immigrationRoutes.studentVisas.en} element={<StudentVisas />} />
              <Route path={immigrationRoutes.studentVisas.es} element={<StudentVisas />} />
              <Route path={immigrationRoutes.studentVisas.vi} element={<StudentVisas />} />
              {/* Criminal Defense Sub-Pages - All Languages */}
              <Route path={criminalDefenseRoutes.dwi.en} element={<DWI />} />
              <Route path={criminalDefenseRoutes.dwi.es} element={<DWI />} />
              <Route path={criminalDefenseRoutes.dwi.vi} element={<DWI />} />
              <Route path={criminalDefenseRoutes.drugCharges.en} element={<DrugCharges />} />
              <Route path={criminalDefenseRoutes.drugCharges.es} element={<DrugCharges />} />
              <Route path={criminalDefenseRoutes.drugCharges.vi} element={<DrugCharges />} />
              <Route path={criminalDefenseRoutes.assault.en} element={<AssaultFamilyViolence />} />
              <Route path={criminalDefenseRoutes.assault.es} element={<AssaultFamilyViolence />} />
              <Route path={criminalDefenseRoutes.assault.vi} element={<AssaultFamilyViolence />} />
              <Route path={criminalDefenseRoutes.violentCrimes.en} element={<ViolentCrimes />} />
              <Route path={criminalDefenseRoutes.violentCrimes.es} element={<ViolentCrimes />} />
              <Route path={criminalDefenseRoutes.violentCrimes.vi} element={<ViolentCrimes />} />
              <Route path={criminalDefenseRoutes.theftRobbery.en} element={<TheftRobbery />} />
              <Route path={criminalDefenseRoutes.theftRobbery.es} element={<TheftRobbery />} />
              <Route path={criminalDefenseRoutes.theftRobbery.vi} element={<TheftRobbery />} />
              <Route path={criminalDefenseRoutes.sexCrimes.en} element={<SexCrimes />} />
              <Route path={criminalDefenseRoutes.sexCrimes.es} element={<SexCrimes />} />
              <Route path={criminalDefenseRoutes.sexCrimes.vi} element={<SexCrimes />} />
              <Route path={criminalDefenseRoutes.expunction.en} element={<ExpunctionNondisclosure />} />
              <Route path={criminalDefenseRoutes.expunction.es} element={<ExpunctionNondisclosure />} />
              <Route path={criminalDefenseRoutes.expunction.vi} element={<ExpunctionNondisclosure />} />
              
              {/* Personal Injury Sub-Pages - All Languages */}
              <Route path={personalInjuryRoutes.autoAccident.en} element={<AutoAccident />} />
              <Route path={personalInjuryRoutes.autoAccident.es} element={<AutoAccident />} />
              <Route path={personalInjuryRoutes.autoAccident.vi} element={<AutoAccident />} />
              <Route path={personalInjuryRoutes.truckAccident.en} element={<TruckAccident />} />
              <Route path={personalInjuryRoutes.truckAccident.es} element={<TruckAccident />} />
              <Route path={personalInjuryRoutes.truckAccident.vi} element={<TruckAccident />} />
              <Route path={personalInjuryRoutes.industrialAccident.en} element={<IndustrialAccident />} />
              <Route path={personalInjuryRoutes.industrialAccident.es} element={<IndustrialAccident />} />
              <Route path={personalInjuryRoutes.industrialAccident.vi} element={<IndustrialAccident />} />
              <Route path={personalInjuryRoutes.medicalMalpractice.en} element={<MedicalMalpractice />} />
              <Route path={personalInjuryRoutes.medicalMalpractice.es} element={<MedicalMalpractice />} />
              <Route path={personalInjuryRoutes.medicalMalpractice.vi} element={<MedicalMalpractice />} />
              
              {/* Family Law Sub-Pages - All Languages */}
              <Route path={familyLawRoutes.divorce.en} element={<Divorce />} />
              <Route path={familyLawRoutes.divorce.es} element={<Divorce />} />
              <Route path={familyLawRoutes.divorce.vi} element={<Divorce />} />
              <Route path={familyLawRoutes.childCustody.en} element={<ChildCustody />} />
              <Route path={familyLawRoutes.childCustody.es} element={<ChildCustody />} />
              <Route path={familyLawRoutes.childCustody.vi} element={<ChildCustody />} />
              <Route path={familyLawRoutes.childSupport.en} element={<ChildSupport />} />
              <Route path={familyLawRoutes.childSupport.es} element={<ChildSupport />} />
              <Route path={familyLawRoutes.childSupport.vi} element={<ChildSupport />} />
              <Route path={familyLawRoutes.cpsDefense.en} element={<CPSDefense />} />
              <Route path={familyLawRoutes.cpsDefense.es} element={<CPSDefense />} />
              <Route path={familyLawRoutes.cpsDefense.vi} element={<CPSDefense />} />
              <Route path={familyLawRoutes.adoption.en} element={<Adoption />} />
              <Route path={familyLawRoutes.adoption.es} element={<Adoption />} />
              <Route path={familyLawRoutes.adoption.vi} element={<Adoption />} />
              <Route path={familyLawRoutes.courtOrderModification.en} element={<CourtOrderModification />} />
              <Route path={familyLawRoutes.courtOrderModification.es} element={<CourtOrderModification />} />
              <Route path={familyLawRoutes.courtOrderModification.vi} element={<CourtOrderModification />} />
              
              {/* Tax Law Sub-Pages - All Languages */}
              <Route path={taxLawRoutes.taxLitigation.en} element={<TaxLitigation />} />
              <Route path={taxLawRoutes.taxLitigation.es} element={<TaxLitigation />} />
              <Route path={taxLawRoutes.taxLitigation.vi} element={<TaxLitigation />} />
              <Route path={taxLawRoutes.taxPlanning.en} element={<TaxPlanning />} />
              <Route path={taxLawRoutes.taxPlanning.es} element={<TaxPlanning />} />
              <Route path={taxLawRoutes.taxPlanning.vi} element={<TaxPlanning />} />
              <Route path={taxLawRoutes.taxDebtResolution.en} element={<TaxDebtResolution />} />
              <Route path={taxLawRoutes.taxDebtResolution.es} element={<TaxDebtResolution />} />
              <Route path={taxLawRoutes.taxDebtResolution.vi} element={<TaxDebtResolution />} />
              
              {/* Probate & Estate Sub-Pages - All Languages */}
              <Route path={probateEstateRoutes.probateAdministration.en} element={<ProbateAdministration />} />
              <Route path={probateEstateRoutes.probateAdministration.es} element={<ProbateAdministration />} />
              <Route path={probateEstateRoutes.probateAdministration.vi} element={<ProbateAdministration />} />
              <Route path={probateEstateRoutes.estatePlanning.en} element={<EstatePlanning />} />
              <Route path={probateEstateRoutes.estatePlanning.es} element={<EstatePlanning />} />
              <Route path={probateEstateRoutes.estatePlanning.vi} element={<EstatePlanning />} />
              <Route path={probateEstateRoutes.specialNeedsTrust.en} element={<SpecialNeedsTrust />} />
              <Route path={probateEstateRoutes.specialNeedsTrust.es} element={<SpecialNeedsTrust />} />
              <Route path={probateEstateRoutes.specialNeedsTrust.vi} element={<SpecialNeedsTrust />} />
              <Route path={probateEstateRoutes.guardianship.en} element={<Guardianship />} />
              <Route path={probateEstateRoutes.guardianship.es} element={<Guardianship />} />
              <Route path={probateEstateRoutes.guardianship.vi} element={<Guardianship />} />
              
              {/* Real Estate Sub-Pages - All Languages */}
              <Route path={realEstateRoutes.buyingSelling.en} element={<BuyingSelling />} />
              <Route path={realEstateRoutes.buyingSelling.es} element={<BuyingSelling />} />
              <Route path={realEstateRoutes.buyingSelling.vi} element={<BuyingSelling />} />
              <Route path={realEstateRoutes.titleSearch.en} element={<TitleSearch />} />
              <Route path={realEstateRoutes.titleSearch.es} element={<TitleSearch />} />
              <Route path={realEstateRoutes.titleSearch.vi} element={<TitleSearch />} />
              <Route path={realEstateRoutes.contractReview.en} element={<ContractReview />} />
              <Route path={realEstateRoutes.contractReview.es} element={<ContractReview />} />
              <Route path={realEstateRoutes.contractReview.vi} element={<ContractReview />} />
              <Route path={realEstateRoutes.affidavitsHeirship.en} element={<AffidavitsHeirship />} />
              <Route path={realEstateRoutes.affidavitsHeirship.es} element={<AffidavitsHeirship />} />
              <Route path={realEstateRoutes.affidavitsHeirship.vi} element={<AffidavitsHeirship />} />
              <Route path={realEstateRoutes.lienRelease.en} element={<LienRelease />} />
              <Route path={realEstateRoutes.lienRelease.es} element={<LienRelease />} />
              <Route path={realEstateRoutes.lienRelease.vi} element={<LienRelease />} />
              <Route path={realEstateRoutes.leaseNegotiations.en} element={<LeaseNegotiations />} />
              <Route path={realEstateRoutes.leaseNegotiations.es} element={<LeaseNegotiations />} />
              <Route path={realEstateRoutes.leaseNegotiations.vi} element={<LeaseNegotiations />} />
              <Route path={realEstateRoutes.closings.en} element={<Closings />} />
              <Route path={realEstateRoutes.closings.es} element={<Closings />} />
              <Route path={realEstateRoutes.closings.vi} element={<Closings />} />
              <Route path={realEstateRoutes.foreclosuresEvictions.en} element={<ForeclosuresEvictions />} />
              <Route path={realEstateRoutes.foreclosuresEvictions.es} element={<ForeclosuresEvictions />} />
              <Route path={realEstateRoutes.foreclosuresEvictions.vi} element={<ForeclosuresEvictions />} />
              
              {/* Business Law Sub-Pages - All Languages */}
              <Route path={businessLawRoutes.businessFormation.en} element={<BusinessFormation />} />
              <Route path={businessLawRoutes.businessFormation.es} element={<BusinessFormation />} />
              <Route path={businessLawRoutes.businessFormation.vi} element={<BusinessFormation />} />
              <Route path={businessLawRoutes.partnershipAgreements.en} element={<PartnershipAgreements />} />
              <Route path={businessLawRoutes.partnershipAgreements.es} element={<PartnershipAgreements />} />
              <Route path={businessLawRoutes.partnershipAgreements.vi} element={<PartnershipAgreements />} />
              <Route path={businessLawRoutes.trademarkRegistration.en} element={<TrademarkRegistration />} />
              <Route path={businessLawRoutes.trademarkRegistration.es} element={<TrademarkRegistration />} />
              <Route path={businessLawRoutes.trademarkRegistration.vi} element={<TrademarkRegistration />} />
              <Route path={businessLawRoutes.purchaseSaleBusiness.en} element={<PurchaseSaleBusiness />} />
              <Route path={businessLawRoutes.purchaseSaleBusiness.es} element={<PurchaseSaleBusiness />} />
              <Route path={businessLawRoutes.purchaseSaleBusiness.vi} element={<PurchaseSaleBusiness />} />
              <Route path={businessLawRoutes.registeredAgent.en} element={<RegisteredAgent />} />
              <Route path={businessLawRoutes.registeredAgent.es} element={<RegisteredAgent />} />
              <Route path={businessLawRoutes.registeredAgent.vi} element={<RegisteredAgent />} />
              <Route path={businessLawRoutes.debtCollection.en} element={<DebtCollection />} />
              <Route path={businessLawRoutes.debtCollection.es} element={<DebtCollection />} />
              <Route path={businessLawRoutes.debtCollection.vi} element={<DebtCollection />} />
              <Route path={businessLawRoutes.insuranceClaims.en} element={<InsuranceClaims />} />
              <Route path={businessLawRoutes.insuranceClaims.es} element={<InsuranceClaims />} />
              <Route path={businessLawRoutes.insuranceClaims.vi} element={<InsuranceClaims />} />
              <Route path={businessLawRoutes.nonprofitFormation.en} element={<NonprofitFormation />} />
              <Route path={businessLawRoutes.nonprofitFormation.es} element={<NonprofitFormation />} />
              <Route path={businessLawRoutes.nonprofitFormation.vi} element={<NonprofitFormation />} />
              <Route path={businessLawRoutes.businessClosing.en} element={<BusinessClosing />} />
              <Route path={businessLawRoutes.businessClosing.es} element={<BusinessClosing />} />
              <Route path={businessLawRoutes.businessClosing.vi} element={<BusinessClosing />} />
              <Route path="/booking/success" element={<BookingSuccess />} />
              
{/* Admin Routes - Protected with AdminRoute */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="appointments" element={<AdminRoute requiredSection="appointments"><AdminAppointments /></AdminRoute>} />
                <Route path="calendar" element={<AdminRoute requiredSection="calendar"><AdminCalendar /></AdminRoute>} />
                <Route path="bookings" element={<AdminRoute requiredSection="appointments"><AdminBookings /></AdminRoute>} />
                <Route path="blog" element={<AdminRoute requiredSection="blog"><AdminBlog /></AdminRoute>} />
                <Route path="pages" element={<AdminRoute requiredSection="pages"><AdminPages /></AdminRoute>} />
                <Route path="payments" element={<AdminRoute requiredSection="payments"><AdminPayments /></AdminRoute>} />
                <Route path="integrations" element={<AdminRoute requiredSection="integrations"><AdminIntegrations /></AdminRoute>} />
                <Route path="settings" element={<AdminRoute requiredSection="settings"><AdminSettings /></AdminRoute>} />
                <Route path="seo-validation" element={<SEOValidation />} />
                <Route path="url-index" element={<URLIndex />} />
                <Route path="sitemap" element={<SiteMap />} />
              </Route>
              
              {/* Tools - All Languages */}
              <Route path={otherRoutes.tools.en} element={<Tools />} />
              <Route path={otherRoutes.tools.es} element={<Tools />} />
              <Route path={otherRoutes.tools.vi} element={<Tools />} />
              
              {/* Blog Posts - All Languages */}
              <Route path={blogRoutes.asylumProcess.en} element={<AsylumProcess2025 />} />
              <Route path={blogRoutes.asylumProcess.es} element={<AsylumProcess2025 />} />
              <Route path={blogRoutes.asylumProcess.vi} element={<AsylumProcess2025 />} />
              <Route path={blogRoutes.deportationDefense.en} element={<DeportationDefense />} />
              <Route path={blogRoutes.deportationDefense.es} element={<DeportationDefense />} />
              <Route path={blogRoutes.deportationDefense.vi} element={<DeportationDefense />} />
              <Route path={blogRoutes.citizenshipGuide.en} element={<CitizenshipGuide />} />
              <Route path={blogRoutes.citizenshipGuide.es} element={<CitizenshipGuide />} />
              <Route path={blogRoutes.citizenshipGuide.vi} element={<CitizenshipGuide />} />
              <Route path={blogRoutes.policeRights.en} element={<PoliceRights />} />
              <Route path={blogRoutes.policeRights.es} element={<PoliceRights />} />
              <Route path={blogRoutes.policeRights.vi} element={<PoliceRights />} />
              <Route path={blogRoutes.personalInjuryClaim.en} element={<PersonalInjuryClaim />} />
              <Route path={blogRoutes.personalInjuryClaim.es} element={<PersonalInjuryClaim />} />
              <Route path={blogRoutes.personalInjuryClaim.vi} element={<PersonalInjuryClaim />} />
              <Route path={blogRoutes.divorceTexas.en} element={<DivorceTexas />} />
              <Route path={blogRoutes.divorceTexas.es} element={<DivorceTexas />} />
              <Route path={blogRoutes.divorceTexas.vi} element={<DivorceTexas />} />
              
              {/* Dynamic Blog Post Route - Must be after static blog routes */}
              <Route path="/blog/:slug" element={<DynamicBlogPost />} />
              <Route path="/es/blog/:slug" element={<DynamicBlogPost />} />
              <Route path="/vi/blog/:slug" element={<DynamicBlogPost />} />
              
              {/* Privacy Policy - All Languages */}
              <Route path={otherRoutes.privacyPolicy.en} element={<PrivacyPolicy />} />
              <Route path={otherRoutes.privacyPolicy.es} element={<PrivacyPolicy />} />
              <Route path={otherRoutes.privacyPolicy.vi} element={<PrivacyPolicy />} />
              
              {/* Terms of Use - All Languages */}
              <Route path={otherRoutes.termsOfUse.en} element={<TermsOfUse />} />
              <Route path={otherRoutes.termsOfUse.es} element={<TermsOfUse />} />
              <Route path={otherRoutes.termsOfUse.vi} element={<TermsOfUse />} />
              
              {/* Areas We Serve - Local SEO Pages - All Languages */}
              <Route path={areaRoutes.theWoodlands.en} element={<TheWoodlands />} />
              <Route path={areaRoutes.theWoodlands.es} element={<TheWoodlands />} />
              <Route path={areaRoutes.theWoodlands.vi} element={<TheWoodlands />} />
              
              <Route path={areaRoutes.riverOaks.en} element={<RiverOaks />} />
              <Route path={areaRoutes.riverOaks.es} element={<RiverOaks />} />
              <Route path={areaRoutes.riverOaks.vi} element={<RiverOaks />} />
              
              <Route path={areaRoutes.montrose.en} element={<Montrose />} />
              <Route path={areaRoutes.montrose.es} element={<Montrose />} />
              <Route path={areaRoutes.montrose.vi} element={<Montrose />} />
              
              <Route path={areaRoutes.heights.en} element={<Heights />} />
              <Route path={areaRoutes.heights.es} element={<Heights />} />
              <Route path={areaRoutes.heights.vi} element={<Heights />} />
              
              <Route path={areaRoutes.westUniversity.en} element={<WestUniversity />} />
              <Route path={areaRoutes.westUniversity.es} element={<WestUniversity />} />
              <Route path={areaRoutes.westUniversity.vi} element={<WestUniversity />} />
              
              <Route path={areaRoutes.sugarLand.en} element={<SugarLand />} />
              <Route path={areaRoutes.sugarLand.es} element={<SugarLand />} />
              <Route path={areaRoutes.sugarLand.vi} element={<SugarLand />} />
              
              <Route path={areaRoutes.katy.en} element={<Katy />} />
              <Route path={areaRoutes.katy.es} element={<Katy />} />
              <Route path={areaRoutes.katy.vi} element={<Katy />} />
              
              <Route path={areaRoutes.midtown.en} element={<Midtown />} />
              <Route path={areaRoutes.midtown.es} element={<Midtown />} />
              <Route path={areaRoutes.midtown.vi} element={<Midtown />} />
              
              <Route path={areaRoutes.medicalCenter.en} element={<MedicalCenter />} />
              <Route path={areaRoutes.medicalCenter.es} element={<MedicalCenter />} />
              <Route path={areaRoutes.medicalCenter.vi} element={<MedicalCenter />} />
              
              <Route path={areaRoutes.memorial.en} element={<Memorial />} />
              <Route path={areaRoutes.memorial.es} element={<Memorial />} />
              <Route path={areaRoutes.memorial.vi} element={<Memorial />} />
              
              <Route path={areaRoutes.cypress.en} element={<Cypress />} />
              <Route path={areaRoutes.cypress.es} element={<Cypress />} />
              <Route path={areaRoutes.cypress.vi} element={<Cypress />} />
              
              <Route path={areaRoutes.uptown.en} element={<Uptown />} />
              <Route path={areaRoutes.uptown.es} element={<Uptown />} />
              <Route path={areaRoutes.uptown.vi} element={<Uptown />} />
              
              <Route path={areaRoutes.downtown.en} element={<Downtown />} />
              <Route path={areaRoutes.downtown.es} element={<Downtown />} />
              <Route path={areaRoutes.downtown.vi} element={<Downtown />} />
              
              <Route path={areaRoutes.energyCorridor.en} element={<EnergyCorridor />} />
              <Route path={areaRoutes.energyCorridor.es} element={<EnergyCorridor />} />
              <Route path={areaRoutes.energyCorridor.vi} element={<EnergyCorridor />} />
              
              <Route path={areaRoutes.greaterSouthwest.en} element={<GreaterSouthwest />} />
              <Route path={areaRoutes.greaterSouthwest.es} element={<GreaterSouthwest />} />
              <Route path={areaRoutes.greaterSouthwest.vi} element={<GreaterSouthwest />} />
              
              <Route path={areaRoutes.springBranch.en} element={<SpringBranch />} />
              <Route path={areaRoutes.springBranch.es} element={<SpringBranch />} />
              <Route path={areaRoutes.springBranch.vi} element={<SpringBranch />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </TooltipProvider>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
